import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Ticket,
  AlertCircle,
  Loader2,
  CheckCircle2,
  ArrowRight,
} from 'lucide-react';
import Navbar from '../../components/Navbar';
import FooterSection from '../../components/FooterSection';
import SEO from '../../components/SEO';
import { LanguageProvider } from '../../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { v: 'billing',     l: 'Billing & Payouts' },
  { v: 'stripe',      l: 'Stripe Connect' },
  { v: 'referrals',   l: 'Referrals & Tracking' },
  { v: 'dashboard',   l: 'Dashboard Issue' },
  { v: 'marketing',   l: 'Marketing Materials' },
  { v: 'account',    l: 'Account / Login' },
  { v: 'technical',   l: 'Technical / Bug' },
  { v: 'other',       l: 'Other' },
];

const PRIORITIES = [
  { v: 'low',     l: 'Low — general question' },
  { v: 'normal',  l: 'Normal — needs response' },
  { v: 'high',    l: 'High — blocking my work' },
  { v: 'urgent',  l: 'Urgent — losing money' },
];

const FIELD =
  'w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.10] text-white placeholder-white/45 text-[14.5px] focus:outline-none focus:border-gold-500/50 transition-colors';
const FIELD_ERR = FIELD + ' border-red-500/60 focus:border-red-500';

export default function PartnersSubmitTicket() {
  return (
    <LanguageProvider>
      <Inner />
    </LanguageProvider>
  );
}

function Inner() {
  const navigate = useNavigate();
  const token = typeof window !== 'undefined' ? localStorage.getItem('nofee_partner_token') : null;
  const [form, setForm] = useState({
    subject: '',
    category: 'billing',
    priority: 'normal',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  // Unauthenticated partners can't open a ticket — we'd have no idea who
  // is reaching out. Send them to the login page with a return URL.
  useEffect(() => {
    if (!token) navigate('/partners/login?next=/partners/support/ticket');
  }, [token, navigate]);

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.subject.trim()) errs.subject = 'Required field';
    if (!form.message.trim() || form.message.trim().length < 12) errs.message = 'Please describe your issue (min. 12 characters)';
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error('Please complete the highlighted fields');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/partners/support/tickets`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDone(r.data);
      toast.success('Ticket submitted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit ticket');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white antialiased">
      <SEO title="Submit a Ticket — NoFee Partners" description="Open a support ticket with the NoFee Partnership Team." path="/partners/support/ticket" noindex />
      <Navbar forceSolid />

      <main className="pt-28 lg:pt-36 pb-24">
        <div className="max-w-[860px] mx-auto px-6 lg:px-8">
          <Link
            to="/partners/dashboard"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/55 hover:text-gold-300 mb-6 transition-colors"
            data-testid="ticket-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Dashboard
          </Link>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold-500/[0.08] border border-gold-500/25 text-[11.5px] uppercase tracking-[0.22em] text-gold-300 font-bold mb-5">
            <Ticket className="w-3.5 h-3.5" /> Support Ticket
          </div>
          <h1 className="text-3xl lg:text-5xl font-black tracking-[-0.02em] text-white leading-[1.05] mb-3" data-testid="ticket-title">
            Submit a <span className="bg-gradient-to-b from-amber-200 to-gold-400 bg-clip-text text-transparent">support ticket</span>.
          </h1>
          <p className="text-white/60 max-w-[640px] mb-10 leading-relaxed">
            Describe your issue with as much detail as you can. Our Partnership Team typically responds within one business day.
          </p>

          {done ? (
            <div className="rounded-3xl border border-gold-500/30 bg-gradient-to-br from-gold-500/[0.06] to-black/30 p-8 lg:p-12 text-center" data-testid="ticket-success">
              <div className="w-16 h-16 rounded-2xl bg-gold-500 text-black flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-black tracking-tight text-white mb-2">Ticket submitted</h2>
              <p className="text-white/65 max-w-[480px] mx-auto mb-2 leading-relaxed">
                Reference: <span className="font-mono text-gold-200">{done.ticket_id || done.id || '—'}</span>
              </p>
              <p className="text-white/60 max-w-[480px] mx-auto mb-7 text-[14.5px]">
                We&rsquo;ll reply by email within one business day. You can keep working in the dashboard while we look into it.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link
                  to="/partners/dashboard"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 text-black font-extrabold text-[15px]"
                  data-testid="ticket-back-dashboard"
                >
                  Back to Dashboard <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/partners/help"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/15 text-white font-bold text-[14px]"
                  data-testid="ticket-back-help"
                >
                  Browse Help Center
                </Link>
              </div>
            </div>
          ) : (
            <form
              onSubmit={submit}
              noValidate
              className="rounded-3xl border border-white/[0.08] bg-gradient-to-b from-zinc-900/50 to-black/30 p-7 lg:p-9 space-y-5"
              data-testid="ticket-form"
            >
              <label className="block">
                <span className="block text-xs font-bold uppercase tracking-[0.18em] text-white/55 mb-2">Subject</span>
                <input
                  value={form.subject}
                  onChange={(e) => update('subject', e.target.value)}
                  placeholder="Short summary of your issue"
                  className={errors.subject ? FIELD_ERR : FIELD}
                  data-testid="ticket-subject"
                />
                {errors.subject && (
                  <p className="mt-1 text-[12px] font-semibold text-red-400 flex items-center gap-1" data-testid="ticket-err-subject">
                    <AlertCircle className="w-3 h-3" /> {errors.subject}
                  </p>
                )}
              </label>

              <div className="grid sm:grid-cols-2 gap-5">
                <label className="block">
                  <span className="block text-xs font-bold uppercase tracking-[0.18em] text-white/55 mb-2">Category</span>
                  <select
                    value={form.category}
                    onChange={(e) => update('category', e.target.value)}
                    className={`${FIELD} appearance-none bg-no-repeat bg-[right_0.85rem_center] bg-[length:14px] pr-9`}
                    style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23a3a3a3' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")" }}
                    data-testid="ticket-category"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.v} value={c.v} className="bg-zinc-900">{c.l}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-xs font-bold uppercase tracking-[0.18em] text-white/55 mb-2">Priority</span>
                  <select
                    value={form.priority}
                    onChange={(e) => update('priority', e.target.value)}
                    className={`${FIELD} appearance-none bg-no-repeat bg-[right_0.85rem_center] bg-[length:14px] pr-9`}
                    style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23a3a3a3' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")" }}
                    data-testid="ticket-priority"
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p.v} value={p.v} className="bg-zinc-900">{p.l}</option>
                    ))}
                  </select>
                </label>
              </div>

              <label className="block">
                <span className="block text-xs font-bold uppercase tracking-[0.18em] text-white/55 mb-2">Message</span>
                <textarea
                  rows={6}
                  value={form.message}
                  onChange={(e) => update('message', e.target.value)}
                  placeholder="Tell us what happened, what you expected, and any steps to reproduce."
                  className={`${errors.message ? FIELD_ERR : FIELD} resize-none`}
                  data-testid="ticket-message"
                />
                {errors.message && (
                  <p className="mt-1 text-[12px] font-semibold text-red-400 flex items-center gap-1" data-testid="ticket-err-message">
                    <AlertCircle className="w-3 h-3" /> {errors.message}
                  </p>
                )}
              </label>

              <div className="flex items-center justify-between gap-4 pt-2">
                <p className="text-xs text-white/45">We answer in the order tickets are received.</p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 disabled:opacity-60 text-black font-extrabold text-[15px] transition-all"
                  data-testid="ticket-submit"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4" />}
                  Submit Ticket
                </button>
              </div>
            </form>
          )}
        </div>
      </main>

      <FooterSection />
    </div>
  );
}
